import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useSnackbar } from "notistack";
import { motion, AnimatePresence } from "framer-motion";

import {
  createTokenOrder,
  getAllTokenActive,
  processPaymentOrder,
} from "src/api/tokenTransaction";

import { useAuth } from "src/hooks/useAuth";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";

import SelectPackage from "./SelectPackage";
import PaymentSection from "./PaymentSection";
import SuccessSection from "./SuccessSection";
import FailedSection from "./FailedSection";

const Container = styled(motion.div)`
  width: 100%;
`;

const TokenTopUp = ({ open }) => {
  let [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState("package");
  const [packageInfoList, setPackageInfoList] = useState([]);
  const defaultData = {
    package: null,
    payment_method: "",
    billing: {
      name: "",
      address: "",
      is_company: false,
      tax_id: "",
    },
  };
  const [formData, setFormData] = useState(defaultData);

  useEffect(() => {
    let paymentStatus = searchParams.get("payment");

    if (paymentStatus === "success") {
      setCurrentSection("success");
    } else if (paymentStatus === "failed" || paymentStatus === "cancelled") {
      setCurrentSection("failed");
    } else {
      setCurrentSection("package");
    }

    fetchTokenPackages();
  }, [searchParams]);

  const fetchTokenPackages = async () => {
    try {
      setIsLoading(true);
      const data = await getAllTokenActive();

      setPackageInfoList(data);
    } catch (error) {
      console.error("Error fetching token packages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPackage = (selectedPackage) => {
    setFormData((prev) => ({
      ...prev,
      package: selectedPackage,
      payment_method: "credit_card",
      billing: {
        ...prev,
        name: user?.shipping_address?.[0]?.name || "",
        address: user?.shipping_address?.[0]?.address || "",
      },
    }));

    setCurrentSection("payment");
  };

  const createOrder = async () => {
    try {
      setIsLoading(true);

      const orderData = await createTokenOrder({
        ...formData,
        packageId: formData.package._id,
      });

      // Process payment immediately after creating the order
      const processPayment = await processPaymentOrder(orderData._id);

      if (!!processPayment) {
        window.open(processPayment?.payment_url);
      }

      // enqueueSnackbar("สร้างคำสั่งซื้อเครดิตสำเร็จ", { variant: "success" });
    } catch (error) {
      console.error("Error creating token order:", error);
      enqueueSnackbar("เกิดข้อผิดพลาดในการสร้างคำสั่งซื้อเครดิต", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
      setFormData(defaultData);
    }
  };

  return (
    <>
      <Main open={open}>
        <AnimatePresence>
          {currentSection === "success" && (
            <Container>
              <SuccessSection />
            </Container>
          )}

          {currentSection === "failed" && (
            <Container>
              <FailedSection />
            </Container>
          )}

          {currentSection === "package" && (
            <Container>
              <SelectPackage
                packageInfoList={packageInfoList}
                handleSelectPackage={handleSelectPackage}
              />
            </Container>
          )}

          {currentSection === "payment" && (
            <Container>
              <PaymentSection
                formData={formData}
                setFormData={setFormData}
                handleBack={() => setCurrentSection("package")}
                handleSubmit={() => createOrder()}
              />
            </Container>
          )}
        </AnimatePresence>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default TokenTopUp;
